import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';
import { IntegratedFlow } from './integrated-flow.service';
import { PagedResponse, PageQuery, toPageParams } from '../models/paged.types';

/** Lightweight row returned by the paged flow instance list. */
export interface FlowInstanceSummary {
  Id: string;
  ProcessId: string;
  ExternalId: string;
  DataContextId?: string | null;
  State: FlowExecutionStatus;
  CreatedAt: number;
  LastUpdatedAt: number;
}

export interface FlowInstance {
  Id: string;
  ProcessId?: string;
  ExternalId?: string;
  CreatedAt: number;
  Revisions?: number;
  Version?: number;
  ArchiveId?: string;
  History?: FlowInstance[];
  CustomData: Record<string, any> | null;
  ExecutedSteps: ExecutedStep[];
  ScheduledSteps: ScheduledStep[];
  InstanceData: Record<string, InstanceData> | null;
  Faults: LogItem[];
  Warnings: LogItem[];
  Logs: LogItem[];
  State: FlowExecutionStatus;
  LastUpdatedAt: number;
  ExecutionContext: FlowExecutionContext;
  DataContextId?: string | null;
}

/** Execution metadata of an executed step. Times are epoch seconds, duration is in seconds. */
export interface ExecutedStepMetaData {
  FlowStepHandlerName?: string;
  FlowStepHandlerVersion?: string;
  StartExecutionTime?: number | null;
  ExecutionCompletedTime?: number | null;
  ExecutionDuration?: number | null;
}

export interface ExecutedStep {
  StepId: string;
  StepType: string;
  StepAddress: string;
  StepExecutionId: string;
  CustomData: Record<string, any>;
  OutputMessage: string | null;
  Output: Record<string, any> | null;
  IsSuccessful: boolean;
  ActivityExecutionId: string | null;
  ActivityId: string | null;
  MetaData?: ExecutedStepMetaData | null;
}

export interface ScheduledStep {
  StepId: string;
  StepType: string;
  StepAddress: string;
  StepExecutionId: string;
  CustomData: Record<string, any> | null;
  ActivityExecutionId: string | null;
}

export interface InstanceData {
  Value: any;
  Type: string;
}

export interface CallbackConfiguration {
  CallbackUrl: string;
  SigningSecret: string;
}

export interface FlowExecutionContext {
  RetryAsOvernightJob: boolean;
  FlowSource: string;
  IsOvernightJob: boolean;
  IsTransientFlow: boolean;
  CallbackConfiguration?: CallbackConfiguration | null;
  CategoryId?: string | null;
  IntegrationId: string;
  IntegrationName: string;
  IntegratedFlow?: IntegratedFlow | null;
  InputData: Record<string, any>;
  DownloadedFiles: Record<string, any>;
  DataContextId?: string | null;
}

export interface LogItem {
  StepId: string;
  Message: string;
  InnerMessage: string;
}

export interface FlowAuditTrailEntry {
  Id: string;
  CreatedAt: number;
  LastUpdatedAt: number;
  ExpireAtUnix: number;
  S3ItemKey: string;
  TenantCode: string;
  FlowId: string;
  EventTime: number;
  Event: FlowAuditTrailEvent;
}

export interface FlowAuditTrailEvent {
  EventId: string;
  EventCode: string;
  EventType: string;
  EventTime: number;
  EventSource: string;
  EventSourceRegion: string;
  TenantId: string;
  EventData: Record<string, unknown>;
  CategoryId: string;
}

export enum FlowExecutionStatus {
  Created = "Created",
  Running = "Running",
  Completed = "Completed",
  Faulted = "Faulted",
  Cancelled = "Cancelled"
}

@Injectable({
  providedIn: 'root'
})
export class FlowService {
  private api: AxiosInstance;
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getAllFlowInstances(
    processId: string | null,
    externalId: string | null,
    state: string | null
  ): Promise<FlowInstance[] | null> {
    try {
      let endpoint = "/flow/instance";
      let params: Record<string, string> = {};

      if (processId) {
        params["ProcessId"] = processId;
      }
      if (externalId) {
        params["ExternalId"] = externalId;
      }
      if (state) {
        params["State"] = state;
      }

      const response = await this.api.get(endpoint, { params });
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowInstancePage(query: PageQuery): Promise<PagedResponse<FlowInstanceSummary>> {
    try {
      const response = await this.api.get("/flow/instance", { params: toPageParams(query) });
      return response.data as PagedResponse<FlowInstanceSummary>;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowInstance(id: string): Promise<FlowInstance | null> {
    try {
      const response = await this.api.get("/flow/instance/" + id);
      return response.data as FlowInstance;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async cancelFlowInstance(id: string): Promise<FlowInstance | null> {
    try {
      const response = await this.api.delete("/flow/instance/" + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async retryFlowInstance(id: string, integrationId: string): Promise<FlowInstance | null> {
    try {
      const response = await this.api.post(integrationId + "/flow/retry/" + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowAuditTrail(flowId: string): Promise<FlowAuditTrailEntry[]> {
    try {
      const response = await this.api.get(`/flow/instance/${flowId}/audit-trail`);
      return response.data as FlowAuditTrailEntry[];
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
